/**
 * The screen wrappers and the modal, held for the extracted code.
 *
 * The demo captures these at load — `const dashEl = document.getElementById("dash")`
 * — and then uses them to answer "is this screen on?" before doing work:
 *
 *     if (!c || !dashEl.classList.contains("on")) { RADAR_RAF = null; return; }
 *
 * In React none of them exist when the module is imported, so the capture is
 * null and the first animation frame takes the screen down. The bindings live
 * here instead, and each screen attaches its own wrapper when it mounts.
 *
 * Nothing assigns to these from the slice — they are only read, and their
 * properties written — so they can safely be imports.
 */
import { useEffect, type RefObject } from "react";

export let modal: HTMLElement | null = null;
export let sheet: HTMLElement | null = null;
export let dashEl: HTMLElement | null = null;
export let landEl: HTMLElement | null = null;
export let bEl: HTMLElement | null = null;
export let rubEl: HTMLElement | null = null;

export type ShellElement = "modal" | "sheet" | "dashEl" | "landEl" | "bEl" | "rubEl";

const setters: Record<ShellElement, (el: HTMLElement | null) => void> = {
  modal: (el) => (modal = el),
  sheet: (el) => (sheet = el),
  dashEl: (el) => (dashEl = el),
  landEl: (el) => (landEl = el),
  bEl: (el) => (bEl = el),
  rubEl: (el) => (rubEl = el),
};

export function attachShell(name: ShellElement, el: HTMLElement | null): void {
  setters[name](el);
}

/**
 * Attach a screen's wrapper for as long as it is mounted.
 *
 * The demo's "is this screen on?" checks read the element's class list, so an
 * unmounted screen must leave nothing behind — otherwise a background loop goes
 * on painting into a canvas that is no longer on the page.
 */
export function useShellElement(
  /** undefined for a screen the demo never captured — then this does nothing */
  name: ShellElement | undefined,
  ref: RefObject<HTMLElement | null>,
): void {
  useEffect(() => {
    if (!name) return;
    attachShell(name, ref.current);
    return () => attachShell(name, null);
  }, [name, ref]);
}

/* ── the engine's tabs, as screens of the app ─────────────────────────────
 *
 * The demo was one page with its own tab bar: `openTab("bldg")` hid the
 * dashboard and showed the buildings tab, `openTab("map")` the airfield. In
 * the app those tabs are screens with routes, and the demo's tab bar and its
 * full-screen facility overlay do not exist. Its "OPEN BUILDINGS INSTEAD" and
 * "OPEN AIRFIELD INSTEAD" buttons hid the dashboard and then asked for an
 * overlay that was not there — an empty page and a null getBoundingClientRect,
 * which read to Adil as the system freezing (2026-10-03).
 *
 * So the app registers its router here, and the demo's openTab asks it first.
 * "dash" is left to the demo: it means "show the dashboard where you are",
 * not "go to another screen".
 */
export const TAB_ROUTE: Record<string, string> = {
  map: "/airfield",
  bldg: "/buildings",
  plans: "/plans",
  landside: "/landside",
  insp: "/inspection-platform",
  fricw: "/friction",
  rubber: "/rubber",
  proj: "/projects",
  assess: "/assessments",
};

let navigator: ((to: string) => void) | null = null;

export function attachNavigator(fn: ((to: string) => void) | null): void {
  navigator = fn;
}

/** Open one of the app's screens by its route. False when there is no router (tests, the bare engine). */
export function navigateToRoute(to: string): boolean {
  if (!navigator) return false;
  navigator(to);
  return true;
}

/** Open the app's screen for one of the demo's tabs. False when there is none, or no router. */
export function navigateToTab(name: string): boolean {
  const to = TAB_ROUTE[name];
  if (!to || !navigator) return false;
  navigator(to);
  return true;
}
